import { useState, useEffect } from "react";
import { getPreferences, updatePreferences, updateTuningConfig, getDeveloperRepos, getDeveloperProjects, getDeveloperConnections, connectGitHub, testGitHubConnection, disconnectDeveloper, addDeveloperRepo, deleteDeveloperRepo, discoverRepos, createDeveloperProject, deleteDeveloperProject } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const AUTH_BASE = import.meta.env.VITE_AUTH_URL || "https://auth.mattherbert.ca";

const PRESETS: Record<string, string> = {
  "Life Assistant": "You are a comprehensive life assistant. You help with personal organization, brainstorming, research, project management, and engaging with external systems. Be proactive, concise, and action-oriented. Use tools when available to get real-time information.",
  Developer: "You are an expert software developer. You help with coding, debugging, architecture decisions, code reviews, and DevOps. Write clean, well-documented code. Explain technical concepts clearly. Use shell_exec and file tools when available.",
  "Business Advisor": "You are a business strategy advisor. You help with go-to-market planning, competitive analysis, financial modeling, pitch decks, and stakeholder communication. Be data-driven and actionable. Provide frameworks and templates when useful.",
  "Creative Writer": "You are a creative writing partner. You help with brainstorming stories, writing copy, editing prose, and developing narrative structure. Match the user's tone and style. Be imaginative but practical.",
};

export function SettingsPanel() {
  const { user } = useAuth();
  const [systemPrompt, setSystemPrompt] = useState("");
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(1.0);
  const [maxTokens, setMaxTokens] = useState(4096);
  const [remoteAllowed, setRemoteAllowed] = useState("allowed");
  const [localPreferred, setLocalPreferred] = useState(false);
  const [routeMode, setRouteMode] = useState("chat.balanced");
  const [dailyBudget, setDailyBudget] = useState("");
  const [monthlyBudget, setMonthlyBudget] = useState("");
  const [autoApproveTools, setAutoApproveTools] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getPreferences()
      .then(({ preferences: p }) => {
        if (!p) return;
        setRemoteAllowed(p.remote_allowed || "allowed");
        setLocalPreferred(!!p.local_preferred);
        setRouteMode(p.preferred_route_mode || "chat.balanced");
        if (p.max_daily_cost_usd) setDailyBudget(String(p.max_daily_cost_usd));
        if (p.max_monthly_cost_usd) setMonthlyBudget(String(p.max_monthly_cost_usd));
        if (p.auto_approve_tools) setAutoApproveTools(true);
      })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePreferences({
        remote_allowed: remoteAllowed,
        local_preferred: localPreferred,
        preferred_route_mode: routeMode,
        max_daily_cost_usd: dailyBudget ? parseFloat(dailyBudget) : null,
        max_monthly_cost_usd: monthlyBudget ? parseFloat(monthlyBudget) : null,
        auto_approve_tools: autoApproveTools,
      });
      await updateTuningConfig({
        scope: "user",
        scope_id: "self",
        system_prompt: systemPrompt || null,
        temperature,
        top_p: topP,
        max_tokens: maxTokens,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold text-foreground mb-6">Settings</h1>

        {/* Profile Section */}
        <Section title="Profile" description="Your account information">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-2xl font-semibold text-primary">
                {(user?.name || user?.email)?.[0]?.toUpperCase() || "?"}
              </span>
            </div>
            <div>
              <div className="text-lg font-medium text-foreground">{user?.name || "Unknown"}</div>
              <div className="text-sm text-muted-foreground">{user?.email}</div>
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded">
                {user?.role}
              </span>
            </div>
          </div>
        </Section>

        {/* System Prompt */}
        <Section title="System Prompt" description="Customize the AI's personality and behavior">
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder="You are a helpful life assistant..."
            rows={4}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex gap-2 mt-2 flex-wrap">
            {Object.keys(PRESETS).map((preset) => (
              <button
                key={preset}
                onClick={() => setSystemPrompt(PRESETS[preset])}
                className="px-3 py-1.5 text-xs font-medium bg-card border border-border rounded-lg text-muted-foreground hover:text-foreground hover:bg-border/50 transition-colors"
              >
                {preset}
              </button>
            ))}
          </div>
        </Section>

        {/* Model Parameters */}
        <Section title="Model Parameters" description="Fine-tune inference behavior">
          <Slider
            label="Temperature"
            value={temperature}
            min={0}
            max={2}
            step={0.1}
            onChange={setTemperature}
          />
          <Slider label="Top P" value={topP} min={0} max={1} step={0.05} onChange={setTopP} />
          <Slider
            label="Max Tokens"
            value={maxTokens}
            min={256}
            max={8192}
            step={256}
            onChange={setMaxTokens}
          />
        </Section>

        {/* Routing Policy */}
        <Section title="Routing Policy" description="Control which models handle your requests">
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Default Route Mode</label>
              <select
                value={routeMode}
                onChange={(e) => setRouteMode(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground"
              >
                <option value="chat.balanced">Balanced</option>
                <option value="chat.fast">Fast</option>
                <option value="chat.best">Best Quality</option>
                <option value="chat.cheapest">Cheapest</option>
                <option value="chat.local_only">Local Only</option>
                <option value="code.agent">Code Agent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Remote Models</label>
              <select
                value={remoteAllowed}
                onChange={(e) => setRemoteAllowed(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground"
              >
                <option value="allowed">Allowed</option>
                <option value="fallback_only">Fallback Only</option>
                <option value="never">Never (Local/Edge Only)</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={localPreferred}
                onChange={(e) => setLocalPreferred(e.target.checked)}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
              />
              Prefer local models when available
            </label>
          </div>
        </Section>

        {/* Tool Execution */}
        <Section title="Tool Execution" description="Control how the AI executes tools like shell commands, file writes, and builds">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={autoApproveTools}
              onChange={(e) => setAutoApproveTools(e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            Auto-approve tool execution
          </label>
          <p className="text-xs text-muted-foreground mt-1.5 ml-6">
            When enabled, the AI can run shell commands, write files, run tests, and deploy without asking for approval each time.
            Disable this if you prefer to review each action before it executes.
          </p>
        </Section>

        {/* Budget Limits */}
        <Section title="Budget Limits" description="Set personal spending caps">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Daily Budget (USD)</label>
              <input
                type="number"
                step="0.50"
                min="0"
                value={dailyBudget}
                onChange={(e) => setDailyBudget(e.target.value)}
                placeholder="No limit"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground font-mono"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Monthly Budget (USD)</label>
              <input
                type="number"
                step="1"
                min="0"
                value={monthlyBudget}
                onChange={(e) => setMonthlyBudget(e.target.value)}
                placeholder="No limit"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground font-mono"
              />
            </div>
          </div>
        </Section>

        {/* Connections */}
        <Section title="Connections" description="Connect external services for tool access">
          <GoogleConnection />
        </Section>

        {/* Developer */}
        <DeveloperSection />

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full md:w-auto px-6 py-2.5 bg-success text-success-foreground font-medium rounded-lg hover:bg-success/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : saved ? "Saved!" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-8">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="text-sm text-muted-foreground mb-3">{description}</p>
      {children}
    </div>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="text-sm text-muted-foreground w-24">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 h-1.5 bg-border rounded-full appearance-none cursor-pointer accent-primary"
      />
      <span className="text-sm font-mono text-foreground w-16 text-right">{value}</span>
    </div>
  );
}

function DeveloperSection() {
  const [ghStatus, setGhStatus] = useState<any>(null);
  const [repos, setRepos] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [patInput, setPATInput] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      testGitHubConnection().catch(() => ({ connected: false })),
      getDeveloperRepos().catch(() => ({ repos: [] })),
      getDeveloperProjects().catch(() => ({ projects: [] })),
    ]).then(([gh, r, p]) => {
      setGhStatus(gh);
      setRepos(r.repos || []);
      setProjects(p.projects || []);
      setLoading(false);
    });
  }, []);

  const handleConnect = async () => {
    if (!patInput.trim()) return;
    setConnecting(true);
    try {
      const result = await connectGitHub(patInput.trim());
      setGhStatus({ connected: true, username: result.username, avatar_url: result.avatar_url });
      setPATInput("");
    } catch (e: any) {
      alert(e.message);
    } finally { setConnecting(false); }
  };

  const handleDisconnect = async () => {
    await disconnectDeveloper("github").catch(() => {});
    setGhStatus({ connected: false });
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const { discovered: d } = await discoverRepos();
      setDiscovered(d || []);
    } catch (e: any) { alert(e.message); }
    finally { setDiscovering(false); }
  };

  const handleAddRepo = async (repo: any) => {
    await addDeveloperRepo({ owner: repo.owner, name: repo.name, description: repo.description, access_level: "read_write" });
    setDiscovered(prev => prev.filter(r => r.name !== repo.name));
    const { repos: updated } = await getDeveloperRepos();
    setRepos(updated || []);
  };

  const handleDeleteRepo = async (id: string) => {
    if (!confirm("Remove this repo?")) return;
    await deleteDeveloperRepo(id);
    setRepos(prev => prev.filter(r => r.id !== id));
  };

  const handleDeleteProject = async (id: string) => {
    if (!confirm("Delete this project?")) return;
    await deleteDeveloperProject(id);
    setProjects(prev => prev.filter(p => p.id !== id));
  };

  if (loading) return <Section title="Developer" description="Loading..."><div /></Section>;

  return (
    <>
      <Section title="GitHub Connection" description="Connect GitHub for code reading, writing, and PR management">
        {ghStatus?.connected ? (
          <div className="flex items-center justify-between p-4 bg-card border border-success/30 rounded-xl">
            <div className="flex items-center gap-3">
              {ghStatus.avatar_url && <img src={ghStatus.avatar_url} className="w-8 h-8 rounded-full" alt="" />}
              <div>
                <div className="text-sm font-medium text-success">GitHub Connected</div>
                <div className="text-xs text-muted-foreground">@{ghStatus.username}</div>
              </div>
            </div>
            <button onClick={handleDisconnect} className="px-3 py-1.5 text-xs font-medium bg-card border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors">
              Disconnect
            </button>
          </div>
        ) : (
          <div className="p-4 bg-card border border-border rounded-xl">
            <p className="text-sm text-muted-foreground mb-3">Enter a GitHub Personal Access Token with <code className="text-primary">repo</code> scope</p>
            <div className="flex gap-2">
              <input
                type="password"
                value={patInput}
                onChange={(e) => setPATInput(e.target.value)}
                placeholder="ghp_..."
                className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground font-mono"
              />
              <button onClick={handleConnect} disabled={connecting || !patInput.trim()} className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50">
                {connecting ? "Connecting..." : "Connect"}
              </button>
            </div>
          </div>
        )}
      </Section>

      <Section title="Repositories" description="Repos the AI can access via GitHub API and ThinkStation">
        <div className="space-y-2">
          {repos.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
              <div>
                <span className="text-sm font-medium text-foreground">{r.owner}/{r.name}</span>
                <span className={`ml-2 px-1.5 py-0.5 text-[10px] rounded ${r.access_level === "read_write" ? "bg-success/20 text-success" : "bg-blue-500/20 text-blue-400"}`}>
                  {r.access_level}
                </span>
                {r.description && <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>}
              </div>
              <button onClick={() => handleDeleteRepo(r.id)} className="text-xs text-muted-foreground hover:text-destructive">×</button>
            </div>
          ))}
        </div>
        {ghStatus?.connected && (
          <button onClick={handleDiscover} disabled={discovering} className="mt-3 px-4 py-2 text-sm font-medium bg-card border border-border rounded-lg text-muted-foreground hover:text-foreground disabled:opacity-50">
            {discovering ? "Discovering..." : "Discover Repos from GitHub"}
          </button>
        )}
        {discovered.length > 0 && (
          <div className="mt-3 space-y-1">
            <p className="text-xs text-muted-foreground">Found {discovered.length} repos not yet registered:</p>
            {discovered.slice(0, 20).map((r) => (
              <div key={r.full_name} className="flex items-center justify-between p-2 bg-background border border-border rounded">
                <div>
                  <span className="text-xs font-medium text-foreground">{r.full_name}</span>
                  {r.language && <span className="ml-2 text-[10px] text-muted-foreground">{r.language}</span>}
                </div>
                <button onClick={() => handleAddRepo(r)} className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90">Add</button>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Projects" description="Multi-repo workspaces for the coding agent">
        <div className="space-y-2">
          {projects.map((p) => (
            <div key={p.id} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
              <div>
                <span className="text-sm font-medium text-foreground">{p.name}</span>
                {p.description && <span className="text-xs text-muted-foreground ml-2">{p.description}</span>}
                <div className="flex gap-1 mt-1">
                  {(p.repos || []).map((r: any) => (
                    <span key={r.name} className={`px-1.5 py-0.5 text-[10px] rounded ${r.is_primary ? "bg-primary/20 text-primary" : "bg-card border border-border text-muted-foreground"}`}>
                      {r.name}
                    </span>
                  ))}
                </div>
              </div>
              <button onClick={() => handleDeleteProject(p.id)} className="text-xs text-muted-foreground hover:text-destructive">×</button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">"Platform" project is auto-generated from all repos.</p>
      </Section>
    </>
  );
}

function GoogleConnection() {
  const [status, setStatus] = useState<{ provider_email?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("infra_token");
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(`${AUTH_BASE}/auth/connections`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const google = (d.connections || []).find((c: any) => c.provider === "google");
        setStatus(google || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    if (window.location.hash.includes("google_connected=true")) {
      window.history.replaceState(null, "", window.location.pathname);
      setStatus({ provider_email: "(just connected)" });
    }
  }, []);

  const handleConnect = () => {
    const token = localStorage.getItem("infra_token");
    const redirectUri = encodeURIComponent(window.location.origin + "/");
    window.location.href = `${AUTH_BASE}/auth/google/connect?redirect_uri=${redirectUri}&infra_session=${token}`;
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Checking connections...</div>;
  }

  if (status) {
    return (
      <div className="flex items-center justify-between p-4 bg-card border border-success/30 rounded-xl">
        <div>
          <div className="text-sm font-medium text-success">Google Connected</div>
          <div className="text-xs text-muted-foreground">
            {status.provider_email} - Gmail, Drive, Calendar
          </div>
        </div>
        <button
          onClick={handleConnect}
          className="px-3 py-1.5 text-xs font-medium bg-card border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors"
        >
          Reconnect
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 bg-card border border-border rounded-xl text-center">
      <p className="text-sm text-muted-foreground mb-3">
        Connect Google to enable Gmail, Drive, and Calendar tools
      </p>
      <button
        onClick={handleConnect}
        className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
      >
        Connect Google
      </button>
    </div>
  );
}
